import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
  createRoom,
  createRoomOnArrival,
  fetchRoomByAppointment,
  fetchAllRooms,
  fetchRoomsByStatus,
  fetchRoomsByDoctor,
  fetchRoomsByPatient,
  fetchRoomById,
  updateRoomStatus,
  deleteRoom,
} from "../thunks/room.thunks";

const initialState = {
  byId: {},     // { [roomId]: room }
  allIds: [],   // [roomId, ...]
  loading: false,
  error: null,
};

const upsertOne = (state, room) => {
  // ✅ le backend retourne roomId (UUID), pas id
  const key = room.roomId ?? room.id;
  if (!key) return;

  state.byId[key] = { ...room, id: key }; // normaliser : toujours avoir .id = roomId
  if (!state.allIds.includes(key)) {
    state.allIds.push(key);
  }
};

const upsertMany = (state, rooms) => {
  rooms.forEach((room) => upsertOne(state, room));
};

const roomSlice = createSlice({
  name: "rooms",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fulfilled: liste
      .addCase(fetchAllRooms.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        upsertMany(state, action.payload || []);
      })
      .addCase(fetchRoomsByStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        upsertMany(state, action.payload || []);
      })
      .addCase(fetchRoomsByDoctor.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        upsertMany(state, action.payload || []);
      })
      .addCase(fetchRoomsByPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        upsertMany(state, action.payload || []);
      })
      // fulfilled: single room
      .addCase(createRoom.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) upsertOne(state, action.payload);
      })
      .addCase(createRoomOnArrival.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) upsertOne(state, action.payload);
      })
      .addCase(fetchRoomByAppointment.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) upsertOne(state, action.payload);
      })
      .addCase(fetchRoomById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) upsertOne(state, action.payload);
      })
      .addCase(updateRoomStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) upsertOne(state, action.payload);
      })
      // delete
      .addCase(deleteRoom.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const { roomId } = action.payload || {};
        if (roomId) {
          delete state.byId[roomId];
          state.allIds = state.allIds.filter((id) => id !== roomId);
        }
      })
      // pending commun
      .addMatcher(
        isAnyOf(
          createRoom.pending,
          createRoomOnArrival.pending,
          fetchRoomByAppointment.pending,
          fetchAllRooms.pending,
          fetchRoomsByStatus.pending,
          fetchRoomsByDoctor.pending,
          fetchRoomsByPatient.pending,
          fetchRoomById.pending,
          updateRoomStatus.pending,
          deleteRoom.pending
        ),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      // rejected commun
      .addMatcher(
        isAnyOf(
          createRoom.rejected,
          createRoomOnArrival.rejected,
          fetchRoomByAppointment.rejected,
          fetchAllRooms.rejected,
          fetchRoomsByStatus.rejected,
          fetchRoomsByDoctor.rejected,
          fetchRoomsByPatient.rejected,
          fetchRoomById.rejected,
          updateRoomStatus.rejected,
          deleteRoom.rejected
        ),
        (state, action) => {
          state.loading = false;
          const payload = action.payload;
          state.error =
            typeof payload === "string"
              ? payload
              : payload?.message || "Erreur lors de l'appel room";
        }
      );
  },
});

export default roomSlice.reducer;